import React from 'react'
import {
  Lightbulb,
  Star,
  TrendingUp,
  DollarSign,
  Users,
  Clock,
  ArrowRight,
  Sparkles,
  Target,
  Zap,
  BarChart3,
  BookOpen
} from 'lucide-react'

// 课程推荐卡片
const CourseRecommendationCard: React.FC<{
  title: string
  description: string
  matchScore: number
  growth: number
  priceRange: string
  demand: 'high' | 'medium' | 'low'
  color: string
}> = ({ title, description, matchScore, growth, priceRange, demand, color }) => {
  const demandConfig = {
    high: { label: '需求旺盛', bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
    medium: { label: '需求稳定', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    low: { label: '新兴领域', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' }
  }

  const d = demandConfig[demand]

  return (
    <div className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl hover:shadow-slate-100/50 transition-all hover:-translate-y-1">
      {/* 顶部渐变 */}
      <div className={`h-1.5 bg-gradient-to-r ${color}`} />

      <div className="p-6">
        {/* 头部 */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform`}
            >
              <Lightbulb className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                {title}
              </h3>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium ${d.bg} ${d.text} ${d.border} border`}
              >
                {d.label}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 rounded-full border border-amber-200">
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            <span className="text-sm font-bold text-amber-700">{matchScore}%</span>
          </div>
        </div>

        {/* 描述 */}
        <p className="text-sm text-slate-600 mb-5 leading-relaxed">{description}</p>

        {/* 数据指标 */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="text-center p-3 bg-emerald-50 rounded-xl border border-emerald-100">
            <div className="flex items-center justify-center gap-1 text-emerald-600 mb-1">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div className="text-lg font-bold text-emerald-700">+{growth}%</div>
            <div className="text-xs text-emerald-600">需求增长</div>
          </div>
          <div className="text-center p-3 bg-indigo-50 rounded-xl border border-indigo-100">
            <div className="flex items-center justify-center gap-1 text-indigo-600 mb-1">
              <DollarSign className="w-4 h-4" />
            </div>
            <div className="text-lg font-bold text-indigo-700">{priceRange}</div>
            <div className="text-xs text-indigo-600">建议定价</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-xl border border-purple-100">
            <div className="flex items-center justify-center gap-1 text-purple-600 mb-1">
              <Users className="w-4 h-4" />
            </div>
            <div className="text-lg font-bold text-purple-700">优</div>
            <div className="text-xs text-purple-600">竞争程度</div>
          </div>
        </div>

        {/* 操作按钮 */}
        <button className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg shadow-indigo-200/50 hover:shadow-xl hover:shadow-indigo-300/50 transition-all">
          开始创建课程
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

export const TeacherCourseRecommendations: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative px-8 py-10 text-center">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">AI 课程推荐</h2>
          <p className="text-white/80">基于市场数据为您推荐最具潜力的课程方向</p>
        </div>
      </div>

      {/* 推荐统计 */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center text-white mx-auto mb-3 shadow-lg">
            <Target className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-slate-900">3</div>
          <div className="text-sm text-slate-500">推荐课程</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white mx-auto mb-3 shadow-lg">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-slate-900">95%</div>
          <div className="text-sm text-slate-500">最高匹配度</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center text-white mx-auto mb-3 shadow-lg">
            <Zap className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-slate-900">热门</div>
          <div className="text-sm text-slate-500">市场趋势</div>
        </div>
      </div>

      {/* 课程推荐列表 */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <CourseRecommendationCard
          title="Python编程入门"
          description="基于当前市场需求和您的专业背景，强烈推荐开设Python编程入门课程。该领域需求旺盛，竞争相对较小。"
          matchScore={95}
          growth={25}
          priceRange="200-350"
          demand="high"
          color="from-indigo-400 to-purple-500"
        />
        <CourseRecommendationCard
          title="数学思维训练"
          description="小学生数学思维训练课程正处于需求上升期，家长对培养孩子逻辑思维能力的需求日益增长。"
          matchScore={88}
          growth={18}
          priceRange="150-280"
          demand="medium"
          color="from-emerald-400 to-teal-500"
        />
        <CourseRecommendationCard
          title="AI绘画创作"
          description="AI艺术创作是新兴热门领域，结合您的技术背景，开设此类课程具有独特优势。"
          matchScore={82}
          growth={45}
          priceRange="180-300"
          demand="low"
          color="from-pink-400 to-rose-500"
        />
      </div>

      {/* 提示信息 */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl p-5 border border-slate-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-slate-200 rounded-xl flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h4 className="font-bold text-slate-800 mb-1">如何使用推荐？</h4>
            <p className="text-sm text-slate-600">
              AI 基于您的教学专长、市场需求和竞争情况为您生成个性化推荐。点击"开始创建课程"即可快速开始课程设计流程。
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
